import React, { useState } from 'react'
import { useQuery, UseQueryResult } from 'react-query'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  Chip,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { Add as AddIcon, Edit as EditIcon, Home as HomeIcon } from '@material-ui/icons'

import {
  useQueryPaginator,
  AddressField,
  DateField,
  DetailTable,
  LinkedField,
  NumberField,
  PaginatedTable,
  RelatedLinkedField,
  RelatedListValueField,
  ShipmentStatusField,
  ValueField,
} from 'src/components/Table'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { ASNDialog } from 'src/modules/client/components/ASNDialog'
import { FreightOrderDialog } from 'src/modules/client/components/freight/OrderDialog'

import { ASN, Tag } from 'src/api/models'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { skuit } from 'src/services/skuit'
import { queryClient } from 'src/App'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const Centered = styled.div`
  text-align: center;
`

interface DetailsProps {
  asn: UseQueryResult<ASN>
  customFieldValues: Record<string, string>
  refresh: () => void
}

const Details = (props: DetailsProps) => {
  const { asn, customFieldValues, refresh } = props
  const [ASNEditDialogOpen, setASNEditDialogOpen] = useState(false)
  return (
    <Card mb={6}>
      {ASNEditDialogOpen && (
        <ASNDialog
          open={ASNEditDialogOpen}
          handleClose={() => {
            setASNEditDialogOpen(false)
          }}
          update
          asn={asn.data}
          onSave={refresh}
        />
      )}
      <CardContent>
        <Centered>
          <HomeIcon />
          <Typography variant="h6" gutterBottom>
            {asn && asn.data?.name}
          </Typography>
          {!!asn && asn.data?.status === 'pending' && (
            <IconButton size="small" onClick={() => setASNEditDialogOpen(true)}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          <DetailTable
            obj={asn}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
              {
                id: 'facility',
                label: 'Facility',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'transloadNumber', label: 'Transload', renderer: ValueField },
              { id: 'carrier', label: 'Carrier', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'consignee', label: 'Consignee', renderer: ValueField },
              { id: 'address', label: 'Address', renderer: AddressField },
              { id: 'expectedPalletCount', label: 'Expected Pallet Count', renderer: ValueField },
              { id: 'status', label: 'Status', renderer: ShipmentStatusField },
              { id: 'details', label: 'Details', renderer: ValueField },
              { id: 'estimatedArrivalDate', label: 'Estimated Arrival Date', renderer: DateField },
              { id: 'receivedOn', label: 'Received On', renderer: DateField },
            ]}
            customFields={customFieldValues}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const ASNPage = () => {
  const { guid } = useParams<PathParams>()
  const asn = useQuery(['asn'], () => skuit.client.asn.retrieve(guid))
  const [addMenuAnchor, setAddMenuAnchor] = React.useState<null | HTMLElement>(null)
  const addMenuOpen = Boolean(addMenuAnchor)
  const [ASNDialogOpen, setASNDialogOpen] = useState(false)
  const [orderDialogOpen, setOrderDialogOpen] = useState(false)
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)

  const attachmentsPaginator = useQueryPaginator(
    'asn-attachments',
    skuit.client.asn.attachments.list,
    {
      params: { page_size: 4, asn: guid },
    }
  )
  const expectedPaginator = useQueryPaginator('asn-expected', skuit.client.asn.expectedUnits.list, {
    params: { page_size: 5, asn: guid },
  })
  const receivedPaginator = useQueryPaginator('asn-units', skuit.client.asn.units.list, {
    params: { page_size: 5, asn: guid },
  })
  const palletsPaginator = useQueryPaginator('pallet-list', skuit.client.pallets.list, {
    params: { page_size: 5, asns: guid },
  })
  const customFieldValuePaginator = useQueryPaginator(
    'custom-field-values',
    skuit.client.asn.customFieldValues.list,
    { params: { asn: guid } }
  )
  const customFieldValues: Record<string, string> = {}
  for (const field of customFieldValuePaginator.data.results) {
    customFieldValues[field.name] = field.value
  }

  const refresh = () => {
    queryClient.invalidateQueries(['asn'])
    attachmentsPaginator.refresh()
    expectedPaginator.refresh()
    customFieldValuePaginator.refresh()
  }

  return (
    <>
      <Helmet title="Advanced Shipping Notice" />
      {ASNDialogOpen && (
        <ASNDialog
          open={ASNDialogOpen}
          handleClose={() => {
            setASNDialogOpen(false)
          }}
        />
      )}
      {orderDialogOpen && (
        <FreightOrderDialog
          open={orderDialogOpen}
          handleClose={() => {
            setOrderDialogOpen(false)
          }}
          asn={asn.data}
        />
      )}
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={refresh}
          addAttachment={(config) =>
            skuit.client.asn.attachments.create(guid, config?.data, config)
          }
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Advanced Shipping Notice
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/asn">
              Advanced Shipping Notices
            </Link>
            <Typography>{asn && asn.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <IconButton
              onClick={(event: React.MouseEvent<HTMLElement>) =>
                setAddMenuAnchor(event.currentTarget)
              }
            >
              <AddIcon />
            </IconButton>
            <Menu
              anchorEl={addMenuAnchor}
              open={addMenuOpen}
              keepMounted
              onClose={() => setAddMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null)
                  setASNDialogOpen(true)
                }}
              >
                New Advanced Shipping Notice
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAddMenuAnchor(null)
                  setOrderDialogOpen(true)
                }}
              >
                Create Freight Order from ASN
              </MenuItem>
            </Menu>
          </div>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Grid container spacing={6}>
            <Grid item xs={12} lg={6}>
              <Details asn={asn} refresh={refresh} customFieldValues={customFieldValues} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <PaginatedFilePreviewGallery
            title="Attachments"
            paginator={attachmentsPaginator}
            getIdent={(item) => item.guid}
            onAdd={() => {
              setAddAttachmentDialogOpen(true)
            }}
            renderItem={(item) => {
              return (
                <>
                  <GenericPreview url={item.file} />
                  <div>
                    {item.tags.map((tag: Tag) => (
                      <Chip key={tag.slug} label={tag.name} />
                    ))}
                  </div>
                </>
              )
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Expected Units"
            fields={[
              { id: 'sku', label: 'Sku', renderer: RelatedLinkedField(ValueField, 'sku') },
              { id: 'sku.productName', label: 'Product', renderer: ValueField },
              { id: 'numUnits', label: 'Number of Units', renderer: NumberField },
            ]}
            paginator={expectedPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Received Units"
            fields={[
              { id: 'sku', label: 'Sku', renderer: RelatedLinkedField(ValueField, 'sku') },
              { id: 'sku.productName', label: 'Product', renderer: ValueField },
              { id: 'numUnits', label: 'Number of Units', renderer: ValueField },
            ]}
            paginator={receivedPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Received Pallets"
            fields={[
              { id: 'name', label: 'Pallet', renderer: LinkedField(ValueField) },
              {
                id: 'clientID',
                label: 'Client Unique ID',
                renderer: ValueField,
              },
              { id: 'skus', label: 'SKUs', renderer: RelatedListValueField('sku') },
              { id: 'totalUnits', label: 'Units Stored on Pallet', renderer: ValueField },
            ]}
            paginator={palletsPaginator}
          />
        </Grid>
      </Grid>
    </>
  )
}
